import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getEmployees } from "services/employees";
import { createMiscDeduction, getMiscDeduction, updateMiscDeduction } from "services/deductions";

const GARNISHMENT_TYPES = [
    { value: "IRS Tax Levy",      label: "IRS Tax Levy" },
    { value: "Federal Student Loan", label: "Federal Student Loan Garnishment" },
    { value: "Creditor Garnishment", label: "Creditor / Court-Ordered Garnishment" },
    { value: "State Tax Levy",    label: "State Tax Levy" },
    { value: "Bankruptcy",        label: "Bankruptcy Wage Deduction" },
    { value: "Other Garnishment", label: "Other Garnishment" },
];

const GARNISHMENT_PREFIX = "[Garnishment]";

const unwrap = (res) => Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);

function toDateInput(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toISOString().split('T')[0];
}

function AddEditGarnishmentDeduction() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('id');
    const isEdit = !!editId;

    const [form, setForm] = useState({
        employeeId: '',
        garnishmentType: 'IRS Tax Levy',
        caseNumber: '',
        issuingAgency: '',
        courtOrderDate: '',
        amount: '',
        maxPercent: '',
        effectiveStart: '',
        effectiveEnd: '',
        notes: '',
    });
    const [mode, setMode] = useState('amount'); // 'amount' | 'percent'
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const { data: employeesRaw = [] } = useQuery({
        queryKey: ['employees'],
        queryFn: async () => unwrap(await getEmployees()),
    });
    const employees = Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []);

    const { data: existing } = useQuery({
        queryKey: ['misc-deduction', editId],
        queryFn: () => getMiscDeduction(editId),
        enabled: isEdit,
    });

    useEffect(() => {
        if (isEdit && existing) {
            const desc = existing.description || '';
            const typeMatch = GARNISHMENT_TYPES.find(g => desc.includes(g.value));
            const caseMatch = desc.match(/Case #([^|]+)/);
            const agencyMatch = desc.match(/Agency: ([^|]+)/);
            const courtMatch = desc.match(/Court Order: ([^|]+)/);
            const notesMatch = desc.match(/Notes: (.+)$/);
            setForm({
                employeeId: existing.employeeId || '',
                garnishmentType: typeMatch?.value || 'IRS Tax Levy',
                caseNumber: caseMatch?.[1]?.trim() || '',
                issuingAgency: agencyMatch?.[1]?.trim() || '',
                courtOrderDate: courtMatch?.[1]?.trim() || '',
                amount: existing.amount || '',
                maxPercent: existing.annualLimitAmount || '',
                effectiveStart: toDateInput(existing.effectiveStart || existing.effective),
                effectiveEnd: toDateInput(existing.effectiveEnd),
                notes: notesMatch?.[1]?.trim() || '',
            });
            if (existing.percent != null && !existing.amount) setMode('percent');
        }
    }, [isEdit, existing]);

    const { mutate: doCreate } = useMutation({
        mutationFn: createMiscDeduction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['misc-deductions'] });
            navigate('/deductions');
        },
        onError: (err) => setError(err?.response?.data?.message || 'Failed to save garnishment.'),
    });

    const { mutate: doUpdate } = useMutation({
        mutationFn: updateMiscDeduction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['misc-deductions'] });
            navigate('/deductions');
        },
        onError: (err) => setError(err?.response?.data?.message || 'Failed to save garnishment.'),
    });

    const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        if (!form.employeeId) return setError('Please select an employee.');
        if (!form.effectiveStart) return setError('Effective start date is required.');
        if (mode === 'amount' && !form.amount) return setError('Amount is required.');
        if (mode === 'percent' && !form.maxPercent) return setError('Max withholding % is required.');

        const descParts = [
            `${GARNISHMENT_PREFIX} ${form.garnishmentType}`,
            form.caseNumber ? `Case #${form.caseNumber}` : null,
            form.issuingAgency ? `Agency: ${form.issuingAgency}` : null,
            form.courtOrderDate ? `Court Order: ${form.courtOrderDate}` : null,
            form.notes ? `Notes: ${form.notes}` : null,
        ].filter(Boolean);

        const payload = {
            employeeId: form.employeeId,
            description: descParts.join(' | '),
            effectiveStart: form.effectiveStart ? new Date(form.effectiveStart).toISOString() : null,
            effectiveEnd: form.effectiveEnd ? new Date(form.effectiveEnd).toISOString() : null,
            amount: mode === 'amount' ? Number(form.amount) : null,
            percent: mode === 'percent' ? Number(form.maxPercent) : null,
            annualLimitAmount: null,
        };

        if (isEdit) {
            doUpdate({ deductionId: editId, ...payload });
        } else {
            doCreate(payload);
        }
    };

    return (
        <div className="content" style={{ padding: "16px 24px 40px 16px" }}>
            <div className="payroll-section">
                <div className="payroll-details-section mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="main-title mb-1">{isEdit ? 'Edit' : 'Add'} Wage Garnishment</h2>
                            <p className="title" style={{ color: '#6c757d', fontSize: 13 }}>
                                Court-ordered wage deductions including IRS tax levies, student loans, and creditor garnishments.
                            </p>
                        </div>
                        <button type="button" className="cancel-button" onClick={() => navigate('/deductions')}>Cancel</button>
                    </div>

                    {error && (
                        <div className="alert alert-danger" style={{ borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Employee & Garnishment Type */}
                        <div className="p-4 rounded-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <h3 className="sub-title mb-3">Garnishment Details</h3>
                            <div className="row g-3">
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>Employee <span style={{ color: "#e87722" }}>*</span></label>
                                    <select className="form-select" value={form.employeeId} onChange={set('employeeId')} required>
                                        <option value="">Select employee...</option>
                                        {employees.map(e => (
                                            <option key={e.id} value={e.id}>
                                                {[e.firstName, e.lastName].filter(Boolean).join(' ') || e.id}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-lg-6">
                                    <label className="form-label" style={{ fontSize: 13 }}>Garnishment Type <span style={{ color: "#e87722" }}>*</span></label>
                                    <select className="form-select" value={form.garnishmentType} onChange={set('garnishmentType')}>
                                        {GARNISHMENT_TYPES.map(g => (
                                            <option key={g.value} value={g.value}>{g.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-lg-4">
                                    <label className="form-label" style={{ fontSize: 13 }}>Case / Order Number</label>
                                    <input type="text" className="form-control" value={form.caseNumber} onChange={set('caseNumber')} placeholder="e.g. 2024-CV-1234" />
                                </div>
                                <div className="col-lg-4">
                                    <label className="form-label" style={{ fontSize: 13 }}>Issuing Agency / Court</label>
                                    <input type="text" className="form-control" value={form.issuingAgency} onChange={set('issuingAgency')} placeholder="e.g. IRS, District Court" />
                                </div>
                                <div className="col-lg-4">
                                    <label className="form-label" style={{ fontSize: 13 }}>Court Order Date</label>
                                    <input type="date" className="form-control" value={form.courtOrderDate} onChange={set('courtOrderDate')} />
                                </div>
                            </div>
                        </div>

                        {/* Amount */}
                        <div className="p-4 rounded-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <h3 className="sub-title mb-3">Withholding Amount</h3>
                            <div className="d-flex gap-3 mb-3">
                                {['amount', 'percent'].map(m => (
                                    <label key={m} style={{ cursor: 'pointer', fontSize: 13 }}>
                                        <input
                                            type="radio"
                                            name="mode"
                                            value={m}
                                            checked={mode === m}
                                            onChange={() => setMode(m)}
                                            style={{ marginRight: 6 }}
                                        />
                                        {m === 'amount' ? 'Fixed Dollar Amount' : 'Percentage of Disposable Earnings'}
                                    </label>
                                ))}
                            </div>
                            <div className="row g-3">
                                {mode === 'amount' ? (
                                    <div className="col-lg-4">
                                        <label className="form-label" style={{ fontSize: 13 }}>Amount Per Pay Period ($) <span style={{ color: "#e87722" }}>*</span></label>
                                        <input type="number" min="0" step="0.01" className="form-control" value={form.amount} onChange={set('amount')} placeholder="0.00" />
                                    </div>
                                ) : (
                                    <div className="col-lg-4">
                                        <label className="form-label" style={{ fontSize: 13 }}>Max Withholding % <span style={{ color: "#e87722" }}>*</span></label>
                                        <input type="number" min="0" max="100" step="0.1" className="form-control" value={form.maxPercent} onChange={set('maxPercent')} placeholder="e.g. 25" />
                                        <div className="form-text" style={{ fontSize: 11 }}>Federal law limits garnishments to 25% of disposable earnings (50–65% for child support).</div>
                                    </div>
                                )}
                                <div className="col-lg-4">
                                    <label className="form-label" style={{ fontSize: 13 }}>Effective Start Date <span style={{ color: "#e87722" }}>*</span></label>
                                    <input type="date" className="form-control" value={form.effectiveStart} onChange={set('effectiveStart')} required />
                                </div>
                                <div className="col-lg-4">
                                    <label className="form-label" style={{ fontSize: 13 }}>End Date (leave blank if ongoing)</label>
                                    <input type="date" className="form-control" value={form.effectiveEnd} onChange={set('effectiveEnd')} />
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="p-4 rounded-3 mb-4" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}>
                            <h3 className="sub-title mb-3">Notes (optional)</h3>
                            <textarea className="form-control" rows={3} value={form.notes} onChange={set('notes')} placeholder="Additional details about this garnishment order..." style={{ resize: 'vertical' }} />
                        </div>

                        <div className="p-3 rounded-3 mb-4" style={{ background: "rgba(232,119,34,0.06)", border: "1px solid rgba(232,119,34,0.2)", fontSize: 12 }}>
                            <strong>Compliance Note:</strong> Wage garnishments are legally mandated withholdings. Failure to comply with a court order or IRS levy can result in employer liability. Consult your legal counsel when processing garnishment orders. Federal law (Title III of the Consumer Credit Protection Act) limits the amount that may be garnished.
                        </div>

                        <div className="d-flex align-items-center gap-3">
                            <button type="submit" className="button-orange" disabled={submitting}>
                                {isEdit ? 'Update Garnishment' : 'Add Garnishment'}
                            </button>
                            <button type="button" className="cancel-button" onClick={() => navigate('/deductions')} disabled={submitting}>Cancel</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default AddEditGarnishmentDeduction;
